(function () {
  "use strict";

  const sizes = [
    { size: 8, mm: 15.2, eu: 48, us: 4.5 },
    { size: 11, mm: 16.2, eu: 51, us: 5.5 },
    { size: 12, mm: 16.5, eu: 52, us: 6 },
    { size: 14, mm: 17.2, eu: 54, us: 7 },
    { size: 16, mm: 17.8, eu: 56, us: 7.5 },
    { size: 18, mm: 18.4, eu: 58, us: 8.5 },
    { size: 20, mm: 19, eu: 60, us: 9 },
    { size: 22, mm: 20, eu: 62, us: 10 }
  ];

  const references = {
    coin_2eur: { type: "circle", mm: 25.75 },
    coin_1eur: { type: "circle", mm: 23.25 },
    coin_50c: { type: "circle", mm: 24.25 },
    card: { type: "card", mm: 85.6 }
  };

  function parseJson(value) {
    try {
      return JSON.parse(value);
    } catch (error) {
      return {};
    }
  }

  function closestSize(diameter) {
    const ordered = [...sizes].sort((first, second) => Math.abs(diameter - first.mm) - Math.abs(diameter - second.mm));
    const best = ordered[0];
    const next = ordered[1];
    const between = Math.abs(diameter - best.mm) <= 0.2 && Math.abs(diameter - next.mm) <= 0.2
      ? { first: best, second: next }
      : null;

    return { best, between };
  }

  function initialize(root) {
    if (!root || root.dataset.emoloInit === "1") return;

    const sectionId = root.dataset.sectionId;
    const i18nNode = root.querySelector("[data-emolo-ring-sizer-i18n]");
    const i18n = parseJson(i18nNode ? i18nNode.textContent : "{}");
    const referenceSelect = root.querySelector(`#emoloRef-${sectionId}`);
    const calibrationSlider = root.querySelector(`#emoloCalibSlider-${sectionId}`);
    const ringSlider = root.querySelector(`#emoloRingSlider-${sectionId}`);
    const calibrationShape = root.querySelector(`#emoloShape-${sectionId}`);
    const ringShape = root.querySelector(`#emoloRing-${sectionId}`);
    const calibrationStage = root.querySelector(".emolo-rs__calib-stage");
    const measureStage = root.querySelector(".emolo-rs__measure-stage");
    const hint = root.querySelector(`#emoloHint-${sectionId}`);
    const calibrationInfo = root.querySelector(`#emoloCalibInfo-${sectionId}`);
    const doneButton = root.querySelector(`#emoloCalibDone-${sectionId}`);
    const millimetersOutput = root.querySelector(`#emoloMm-${sectionId}`);
    const sizeOutput = root.querySelector(`#emoloSize-${sectionId}`);
    const diameterOutput = root.querySelector(`#emoloDia-${sectionId}`);
    const equivalentOutput = root.querySelector(`#emoloEquiv-${sectionId}`);
    const stepButtons = [...root.querySelectorAll(".emolo-rs__step")];
    const panels = [...root.querySelectorAll(".emolo-rs__panel")];

    if (!referenceSelect || !calibrationSlider || !ringSlider || !calibrationShape || !ringShape || !calibrationStage || !measureStage || !doneButton) return;

    root.dataset.emoloInit = "1";
    let pixelsPerMillimeter = null;

    function stageLimit(stage) {
      return Math.max(120, Math.floor(stage.clientWidth - 48));
    }

    function displayedSize(slider, stage) {
      return Math.min(Number(slider.value), stageLimit(stage));
    }

    function setStep(step, moveFocus) {
      panels.forEach((panel) => {
        const active = panel.dataset.panel === String(step);
        panel.classList.toggle("emolo-rs__panel--active", active);
        panel.setAttribute("aria-hidden", active ? "false" : "true");
      });

      stepButtons.forEach((button) => {
        const active = button.dataset.step === String(step);
        button.classList.toggle("emolo-rs__step--active", active);
        button.setAttribute("aria-selected", active ? "true" : "false");
        button.tabIndex = active ? 0 : -1;
        if (active && moveFocus) button.focus();
      });
    }

    function toggleMeasureStep(enabled) {
      const measureButton = stepButtons.find((button) => button.dataset.step === "2");
      if (measureButton) measureButton.disabled = !enabled;
    }

    function updateCalibrationShape() {
      const reference = references[referenceSelect.value] || references.coin_2eur;
      const pixels = displayedSize(calibrationSlider, calibrationStage);
      calibrationShape.style.width = `${pixels}px`;
      calibrationShape.style.height = reference.type === "card" ? `${Math.round(pixels * 0.62)}px` : `${pixels}px`;
      calibrationShape.style.borderRadius = reference.type === "card" ? "var(--emolo-surface-radius, .75rem)" : "999px";
      calibrationSlider.setAttribute("aria-valuetext", `${pixels} px`);
    }

    function formatBetween(first, second) {
      return (i18n.between || "{a} – {b}")
        .replace("{a}", first)
        .replace("{b}", second)
        .concat(` ${i18n.note_between || ""}`)
        .trim();
    }

    function formatEquivalent(eu, us) {
      return (i18n.equiv || "EU {eu} · US {us}")
        .replace("{eu}", eu)
        .replace("{us}", us);
    }

    function formatDiameter(value) {
      return (i18n.diameter_value || "Ø {mm} mm").replace("{mm}", value.toFixed(1));
    }

    function updateRing() {
      const pixels = displayedSize(ringSlider, measureStage);
      ringShape.style.width = `${pixels}px`;
      ringShape.style.height = `${pixels}px`;
      ringSlider.setAttribute("aria-valuetext", `${pixels} px`);

      if (!pixelsPerMillimeter) {
        if (millimetersOutput) millimetersOutput.textContent = "— mm";
        if (sizeOutput) sizeOutput.textContent = "—";
        if (diameterOutput) diameterOutput.textContent = "—";
        if (equivalentOutput) equivalentOutput.textContent = i18n.calibrate_first || "";
        return;
      }

      const diameter = pixels / pixelsPerMillimeter;
      const result = closestSize(diameter);

      if (millimetersOutput) millimetersOutput.textContent = `${diameter.toFixed(1)} mm`;
      if (sizeOutput) sizeOutput.textContent = result.best.size;
      if (diameterOutput) {
        const label = i18n.diameter_label ? `${i18n.diameter_label} ` : "";
        diameterOutput.textContent = `${label}${formatDiameter(result.best.mm)}`;
      }
      if (equivalentOutput) {
        equivalentOutput.textContent = result.between
          ? formatBetween(result.between.first.size, result.between.second.size)
          : formatEquivalent(result.best.eu, result.best.us);
      }

      ringSlider.setAttribute("aria-valuetext", `${diameter.toFixed(1)} mm`);
    }

    function setRingRange() {
      if (!pixelsPerMillimeter) return;

      const minimum = Math.round((sizes[0].mm - 1) * pixelsPerMillimeter);
      const maximum = Math.min(Math.round((sizes[sizes.length - 1].mm + 1) * pixelsPerMillimeter), stageLimit(measureStage));
      ringSlider.min = String(minimum);
      ringSlider.max = String(Math.max(minimum + 1, maximum));
      ringSlider.value = String(Math.round((Number(ringSlider.min) + Number(ringSlider.max)) / 2));
    }

    function resetReference() {
      const reference = references[referenceSelect.value] || references.coin_2eur;
      pixelsPerMillimeter = null;
      hint.textContent = reference.type === "card" ? i18n.hint_card || "" : i18n.hint_coin || "";
      if (calibrationInfo) calibrationInfo.textContent = i18n.calibrate_first || "";
      toggleMeasureStep(false);
      setStep(1, false);
      updateCalibrationShape();
      updateRing();
    }

    function finishCalibration() {
      const reference = references[referenceSelect.value] || references.coin_2eur;
      const renderedPixels = calibrationShape.getBoundingClientRect().width;
      pixelsPerMillimeter = renderedPixels / reference.mm;
      setStep(2, false); setRingRange();
      toggleMeasureStep(true);
      if (calibrationInfo) calibrationInfo.textContent = i18n.result_live || "";
      setStep(2, true);
      updateRing();
    }

    referenceSelect.addEventListener("change", resetReference);
    calibrationSlider.addEventListener("input", updateCalibrationShape);
    ringSlider.addEventListener("input", updateRing);
    doneButton.addEventListener("click", finishCalibration);

    stepButtons.forEach((button) => {
      button.addEventListener("click", () => {
        if (!button.disabled) setStep(Number(button.dataset.step), false);
      });

      button.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        const enabledButtons = stepButtons.filter((item) => !item.disabled);
        if (enabledButtons.length < 2) return;

        event.preventDefault();
        const currentIndex = enabledButtons.indexOf(button);
        let nextIndex = event.key === "Home" ? 0 : enabledButtons.length - 1;
        if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % enabledButtons.length;
        if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + enabledButtons.length) % enabledButtons.length;
        setStep(Number(enabledButtons[nextIndex].dataset.step), true);
      });
    });

    const resizeObserver = new ResizeObserver(() => {
      updateCalibrationShape();
      updateRing();
    });
    resizeObserver.observe(calibrationStage);
    resizeObserver.observe(measureStage);

    toggleMeasureStep(false);
    resetReference();
  }

  function boot(scope) {
    if (scope.matches && scope.matches("[data-emolo-ring-sizer]")) initialize(scope);
    scope.querySelectorAll("[data-emolo-ring-sizer]").forEach(initialize);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener("shopify:section:load", (event) => boot(event.target));
})();
